import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'console.active-context';

export type ActiveContext = { tenantId: string; organizationId: string };

let activeContext: ActiveContext | null = null;
const listeners = new Set<() => void>();

function isActiveContext(value: unknown): value is ActiveContext {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).tenantId === 'string' &&
    typeof (value as Record<string, unknown>).organizationId === 'string'
  );
}

/** Parses `localStorage`'s persisted value defensively — any malformed shape resolves to `null`. */
export function readPersistedActiveContext(): ActiveContext | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    return isActiveContext(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function getActiveContext(): ActiveContext | null {
  return activeContext;
}

/**
 * Updates the module-level active context, persists it to `localStorage` (or clears the key when
 * `null`), and notifies subscribers. This is a user SELECTION store, never an authorization cache
 * (FR-4.6/FR-6.3) — it holds only the (Tenant, Organization) pair the caller picked; the server
 * re-derives and enforces every permission per request.
 */
export function setActiveContext(context: ActiveContext | null): void {
  activeContext = context;
  if (context === null) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  }
  for (const listener of listeners) {
    listener();
  }
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The active context as React state: a render follows every switch of organization. */
export function useActiveContextValue(): ActiveContext | null {
  return useSyncExternalStore(subscribe, getActiveContext, getActiveContext);
}
